import {
  ADMIN_BLOGS,
  ADMIN_BRANDS,
  ADMIN_CONCIERGE,
  ADMIN_DASHBOARD,
  ADMIN_LOCATIONS,
  ADMIN_PAYOUTS,
  ADMIN_PRIVACY_POLICY,
  ADMIN_TERMS_AND_CONDITIONS,
  ADMIN_USERS,
  ADMIN_VERIFICATIONS,
  ADMIN_WEBSITES,
  Disclaimer,
  Div,
  H3,
  H5,
  MenuIcon,
  P,
  useSidebarState,
} from '@vgrubs/components';
import { useLocation } from 'react-router';
import styled from 'styled-components';
import { NotificationsBell } from '../../../adminPages/Notifications/NotificationsBell';

const AdminHeaderStyled = styled.div`
  display: flex;
  justify-content: space-between;
  height: 75px;
  align-items: center;
  gap: 50px;
  border-bottom: ${({ theme }) => theme.borders.border1};

  @media (max-width: 800px) {
    height: 60px;
    gap: 8px;
  }
`;

export const AdminHeader = ({
  children,
  title,
  subtitle,
  subtitleSmall,
}: {
  children?: React.ReactNode;
  title?: string;
  subtitle?: string | React.ReactNode;
  /**
   * Render the subtitle in body text rather than the H5 heading - for a
   * subtitle that names the context (an organization, an account) rather
   * than continuing the title.
   */
  subtitleSmall?: boolean;
}) => {
  const location = useLocation();

  const renderTitle = () => {
    const WEBSITES = 'Websites';
    const DASHBOARD = 'Dashboard';
    const VERIFICATIONS = 'Verifications';
    const USERS = 'Users';
    const LOCATIONS = 'Locations';
    const BLOGS = 'Blogs';
    const BRANDS = 'Brands';
    const CONCIERGE = 'Concierge';
    const PRIVACY_POLICY = 'Privacy Policy';
    const TERMS_AND_CONDITIONS = 'Terms & Conditions';
    const PAYOUTS = 'Payouts';

    const pathnameStartWith = (path: string) =>
      location.pathname.startsWith(path) ? location.pathname : null;

    switch (location.pathname) {
      case pathnameStartWith(ADMIN_DASHBOARD):
        return DASHBOARD;
      case pathnameStartWith(ADMIN_VERIFICATIONS):
        return VERIFICATIONS;
      case pathnameStartWith(ADMIN_USERS):
        return USERS;
      case pathnameStartWith(ADMIN_LOCATIONS):
        return LOCATIONS;
      case pathnameStartWith(ADMIN_WEBSITES):
        return WEBSITES;
      case pathnameStartWith(ADMIN_BLOGS):
        return BLOGS;
      case pathnameStartWith(ADMIN_BRANDS):
        return BRANDS;
      case pathnameStartWith(ADMIN_CONCIERGE):
        return CONCIERGE;
      case pathnameStartWith(ADMIN_PRIVACY_POLICY):
        return PRIVACY_POLICY;
      case pathnameStartWith(ADMIN_TERMS_AND_CONDITIONS):
        return TERMS_AND_CONDITIONS;
      case pathnameStartWith(ADMIN_PAYOUTS):
        return PAYOUTS;

      default:
        break;
    }
  };

  const {
    sidebarIsOpen: sidebarIsOpenState,
    setSidebarIsOpen: setSidebarIsOpenState,
  } = useSidebarState((state) => state);
  const sidebarIsOpen = sidebarIsOpenState;
  const setSidebarIsOpen = (value: boolean) => setSidebarIsOpenState(value);

  return (
    <AdminHeaderStyled>
      <Div $flex $gap="16px" $alignCenter>
        <MenuIcon
          isOpen={sidebarIsOpen}
          onClick={() => setSidebarIsOpen(!sidebarIsOpen)}
        />

        <Div $desktop>
          <H3 $m="0">
            <span>{title || renderTitle()}</span>
          </H3>
          {subtitle &&
            (subtitleSmall ? (
              <P $m="0">{subtitle}</P>
            ) : (
              <H5 $m="0">{subtitle}</H5>
            ))}
        </Div>
        <Div $mobile>
          <H5 $m="0">
            <span>{title || renderTitle()}</span>
          </H5>

          {subtitle && (
            <Disclaimer $m="0" $bold={!subtitleSmall}>
              {subtitle}
            </Disclaimer>
          )}
        </Div>
      </Div>

      {/* The bell goes last, at the far right of every page: the page's own
          actions keep the wrapper they had, positioned children and all. */}
      <Div $flex $alignCenter $gap="20px" $gapSM="10px">
        <Div $relative>{children}</Div>
        <NotificationsBell />
      </Div>
    </AdminHeaderStyled>
  );
};
